import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Stack,
  Button,
  TextField,
  Box,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Card,
  CardContent,
  Grid,
  Alert,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const DefineInterviewProcess = ({
  interviewStages,
  setInterviewStages,
  onNext,
  onBack,
}) => {
  const [currentStage, setCurrentStage] = useState({
    stageName: "",
    interviewers: [],
  });
  const [currentInterviewer, setCurrentInterviewer] = useState("");
  const [error, setError] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const navigate = useNavigate();

  const handleAddInterviewer = () => {
    const trimmedInterviewer = currentInterviewer.trim();
    if (!trimmedInterviewer) return;

    if (currentStage.interviewers.includes(trimmedInterviewer)) {
      setError("Interviewer already added");
      return;
    }

    setCurrentStage({
      ...currentStage,
      interviewers: [...currentStage.interviewers, trimmedInterviewer],
    });
    setCurrentInterviewer("");
    setError("");
  };

  const handleRemoveInterviewer = (interviewerToRemove) => {
    setCurrentStage({
      ...currentStage,
      interviewers: currentStage.interviewers.filter(
        (i) => i !== interviewerToRemove,
      ),
    });
  };

  const handleAddStage = () => {
    const trimmedStageName = currentStage.stageName.trim();
    const trimmedInterviewer = currentInterviewer.trim();

    if (!trimmedStageName) {
      setError("Stage name is required");
      return;
    }

    // Prepare the final list of interviewers
    let finalInterviewers = [...currentStage.interviewers];
    if (trimmedInterviewer) {
      if (!finalInterviewers.includes(trimmedInterviewer)) {
        finalInterviewers.push(trimmedInterviewer);
      }
    }

    if (finalInterviewers.length === 0) {
      setError("At least one interviewer is required");
      return;
    }

    // Prevent duplicate stage names, case insensitive
    const isDuplicateName = interviewStages.some((stage, index) => {
      // while editing, allow the same name for the same index
      if (editingIndex !== null && index === editingIndex) return false;
      return (
        stage.stageName.trim().toLowerCase() === trimmedStageName.toLowerCase()
      );
    });

    if (isDuplicateName) {
      setError("Stage name must be unique");
      return;
    }

    if (editingIndex !== null) {
      // Update existing stage
      const updatedStages = [...interviewStages];
      updatedStages[editingIndex] = {
        stageName: trimmedStageName,
        interviewers: finalInterviewers,
      };
      setInterviewStages(updatedStages);
      setEditingIndex(null);
    } else {
      // Add new stage
      setInterviewStages([
        ...interviewStages,
        {
          stageName: trimmedStageName,
          interviewers: finalInterviewers,
        },
      ]);
    }

    setCurrentStage({ stageName: "", interviewers: [] });
    setCurrentInterviewer("");
    setError("");
  };

  const handleEditStage = (index) => {
    // Handle legacy data where interviewer might be a string
    const stage = interviewStages[index];
    const interviewers = Array.isArray(stage.interviewers)
      ? stage.interviewers
      : stage.interviewer
        ? [stage.interviewer]
        : [];

    setCurrentStage({
      stageName: stage.stageName,
      interviewers: interviewers,
    });
    setEditingIndex(index);
    setError("");
  };

  const handleCancelEdit = () => {
    setCurrentStage({ stageName: "", interviewers: [] });
    setCurrentInterviewer("");
    setEditingIndex(null);
    setError("");
  };

  const handleRemoveStage = (index) => {
    setInterviewStages(interviewStages.filter((_, i) => i !== index));
    // If we're editing the stage being removed, clear the form
    if (editingIndex === index) {
      setCurrentStage({ stageName: "", interviewers: [] });
      setCurrentInterviewer("");
      setEditingIndex(null);
    } else if (editingIndex !== null && editingIndex > index) {
      // Adjust editing index if a stage before it was removed
      setEditingIndex(editingIndex - 1);
    }
  };

  const handleNext = () => {
    if (interviewStages.length === 0) {
      setError("Please add at least one interview stage");
      return;
    }
    setError("");
    onNext();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 6 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/corporate/job-postings")}
        sx={{ mb: 2 }}
      >
        Back to Job Postings
      </Button>
      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Stepper activeStep={3} alternativeLabel sx={{ mb: 4 }}>
          <Step>
            <StepLabel>Create a role</StepLabel>
          </Step>
          <Step>
            <StepLabel>Set skills</StepLabel>
          </Step>
          <Step>
            <StepLabel>Company type & culture Fit</StepLabel>
          </Step>
          <Step>
            <StepLabel>Define interview process</StepLabel>
          </Step>
          <Step>
            <StepLabel>Review & Publish</StepLabel>
          </Step>
        </Stepper>

        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          Define Interview Process
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Add interview stages for this position. Each stage should have a name
          and assigned interviewers.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Add Stage Form */}
        <Box sx={{ mb: 4, p: 3, bgcolor: "grey.50", borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            {editingIndex !== null
              ? "Edit Interview Stage"
              : "Add Interview Stage"}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <TextField
                label="Stage Name"
                placeholder="e.g., Technical Interview, HR Round"
                value={currentStage.stageName}
                onChange={(e) =>
                  setCurrentStage({
                    ...currentStage,
                    stageName: e.target.value,
                  })
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label={
                    currentStage.interviewers.length > 0
                      ? "Add Another Interviewer"
                      : "Add Interviewer"
                  }
                  placeholder="e.g., John Doe"
                  value={currentInterviewer}
                  onChange={(e) => setCurrentInterviewer(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddInterviewer();
                    }
                  }}
                  fullWidth
                  size="small"
                />
                <Button
                  variant="outlined"
                  onClick={handleAddInterviewer}
                  sx={{
                    minWidth: "70px",
                    height: "40px",
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    fontSize: "0.75rem",
                    lineHeight: 1.1,
                    textTransform: "none",
                  }}
                >
                  {currentStage.interviewers.length > 0
                    ? "Add Another\nInterviewer"
                    : "Add\nInterviewer"}
                </Button>
              </Stack>
              <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {currentStage.interviewers.map((interviewer, idx) => (
                  <Chip
                    key={idx}
                    label={interviewer}
                    onDelete={() => handleRemoveInterviewer(interviewer)}
                    onClick={() => {
                      setCurrentInterviewer(interviewer);
                      handleRemoveInterviewer(interviewer);
                    }}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={editingIndex !== null ? null : <AddIcon />}
                  onClick={handleAddStage}
                  fullWidth
                  sx={{ height: "40px" }}
                >
                  {editingIndex !== null ? "Update" : "Add Stage"}
                </Button>
                {editingIndex !== null && (
                  <Button
                    variant="outlined"
                    onClick={handleCancelEdit}
                    sx={{ height: "40px", px: 1 }}
                  >
                    Cancel
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* Display Added Stages */}
        {interviewStages.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Interview Stages ({interviewStages.length})
            </Typography>
            <Stack spacing={2}>
              {interviewStages.map((stage, index) => (
                <Card
                  key={index}
                  variant="outlined"
                  sx={{
                    "&:hover": {
                      boxShadow: 2,
                    },
                    bgcolor:
                      editingIndex === index ? "action.hover" : "inherit",
                  }}
                >
                  <CardContent>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={1}>
                        <Typography
                          variant="h6"
                          color="primary"
                          sx={{ fontWeight: 600 }}
                        >
                          {index + 1}
                        </Typography>
                      </Grid>
                      <Grid item xs={4.5}>
                        <Typography variant="body2" color="text.secondary">
                          Stage Name
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {stage.stageName}
                        </Typography>
                      </Grid>
                      <Grid item xs={4.5}>
                        <Typography variant="body2" color="text.secondary">
                          Interviewers
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {Array.isArray(stage.interviewers)
                            ? stage.interviewers.join(", ")
                            : stage.interviewer}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                        >
                          <IconButton
                            color="primary"
                            onClick={() => handleEditStage(index)}
                            size="small"
                            disabled={
                              editingIndex !== null && editingIndex !== index
                            }
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveStage(index)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        )}

        {interviewStages.length === 0 && (
          <Box
            sx={{
              mb: 4,
              p: 4,
              textAlign: "center",
              bgcolor: "grey.50",
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No interview stages added yet. Add at least one stage to continue.
            </Typography>
          </Box>
        )}

        {/* Navigation Buttons */}
        <Stack direction="row" justifyContent="space-between">
          <Button variant="outlined" color="secondary" onClick={onBack}>
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={interviewStages.length === 0}
          >
            Next
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
};

export default DefineInterviewProcess;
